import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import InstitutionNavbar from '../components/instituteNavbar';
import {
    Mail, Bookmark, MapPin, User, ExternalLink
} from 'lucide-react';

const JobApplicants = () => {
    const { jobId } = useParams();
    const [apiCandidates, setApiCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [jobTitle, setJobTitle] = useState("");

    // State to manage local UI updates for actions
    const [candidatesState, setCandidatesState] = useState({});

    useEffect(() => {
        const fetchJobApplicants = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getJobApplicants/${jobId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setApiCandidates(response.data);
            } catch (err) {
                console.error("Error fetching job applicants:", err);
                setError("Failed to load applicants.");
                setApiCandidates([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchJobDetails = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/getJob/${jobId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.data) {
                    setJobTitle(response.data.data.title);
                }
            } catch (err) {
                console.error("Error fetching job details:", err);
            }
        }

        fetchJobApplicants();
        fetchJobDetails();
    }, [jobId]);


    const handleSave = async (candidate) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/institutionfilter/save-candidate`,
                { candidateId: candidate._id || candidate.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const isSaved = response.data.isSaved;
            setCandidatesState(prev => ({
                ...prev,
                [candidate._id || candidate.id]: { ...prev[candidate._id || candidate.id], is_saved: isSaved }
            }));

        } catch (err) {
            console.error(err);
            alert("Error saving candidate");
        }
    };

    const handleInvite = async (candidate) => {
        try {
            const token = localStorage.getItem("token");
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/institutionfilter/invite-candidate`,
                { candidateId: candidate._id || candidate.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setCandidatesState(prev => ({
                ...prev,
                [candidate._id || candidate.id]: { ...prev[candidate._id || candidate.id], is_invited: true }
            }));
            alert("Invitation sent!");

        } catch (err) {
            console.error(err);
            alert("Error inviting candidate");
        }
    };

    const isSaved = (candidate) => {
        return candidatesState[candidate._id || candidate.id]?.is_saved ?? candidate.is_saved;
    };

    const isInvited = (candidate) => {
        return candidatesState[candidate._id || candidate.id]?.is_invited ?? candidate.is_invited;
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <InstitutionNavbar />

            <div className="pt-24 px-4 md:px-8 max-w-7xl mx-auto w-full">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                    Candidates Applied for - <span className="text-blue-600">{jobTitle}</span>
                </h2>

                {loading && <div className="text-center py-10">Loading applicants...</div>}
                {error && <div className="text-center py-10 text-red-500">{error}</div>}

                {!loading && !error && apiCandidates.length === 0 && (
                    <div className="text-center py-10 text-gray-500">No applicants found for this job yet.</div>
                )}

                <div className="grid grid-cols-1 gap-6">
                    {apiCandidates.map((candidate) => {
                        const saved = isSaved(candidate);
                        const invited = isInvited(candidate);

                        const qualification = candidate.education?.length > 0 ? (candidate.education[0].degree || candidate.education[0].education) : "Not Specified";
                        const experienceVal = candidate.experience || candidate.work_preference?.experience || 0;
                        const noticeVal = candidate.notice_period || candidate.work_preference?.notice_period || "Immediate";


                        return (
                            <div key={candidate._id} className="bg-white rounded-lg shadow-sm p-6 flex flex-col md:flex-row gap-6 border border-gray-100">
                                {/* Profile Picture */}
                                <div className="shrink-0">
                                    {candidate.profilePic ? (
                                        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-100">
                                            <img
                                                src={`${process.env.REACT_APP_API_URL}/${candidate.profilePic.replace(/\\/g, "/")}`}
                                                className="w-full h-full object-cover"
                                                alt="Profile"
                                                onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${candidate.first_name}&background=random` }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
                                            <User size={40} />
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <h3 className="text-xl font-semibold text-blue-600">
                                            {candidate.first_name} {candidate.last_name}
                                        </h3>
                                    </div>

                                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-4">
                                        <MapPin size={14} /> {candidate.address?.area ? `${candidate.address.area}, ` : ""}{candidate.address?.city || candidate.city || "Location N/A"}
                                    </p>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-4 text-sm text-gray-700">
                                        <p><span className="font-medium text-gray-500">Qualification:</span> {qualification}</p>
                                        <p><span className="font-medium text-gray-500">Experience:</span> {experienceVal} Years</p>
                                        <p><span className="font-medium text-gray-500">Notice Period:</span> {noticeVal}</p>

                                    </div>
                                </div>

                                <div className="flex flex-col gap-2 justify-center min-w-[140px]">

                                    <button
                                        onClick={() => handleInvite(candidate)}
                                        disabled={invited}
                                        className={`flex items-center justify-center gap-2 px-4 py-2 border text-sm font-medium rounded transition-colors ${invited ? 'bg-gray-100 text-gray-400' : 'border-blue-900 text-blue-900 hover:bg-blue-50'}`}
                                    >
                                        <Mail size={16} /> {invited ? "Invited" : "Invite"}
                                    </button>

                                    <button
                                        onClick={() => handleSave(candidate)}
                                        className={`flex items-center justify-center gap-2 px-4 py-2 border text-sm font-medium rounded transition-colors ${saved ? 'bg-blue-50 text-blue-700 border-blue-200' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        <Bookmark size={16} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save"}
                                    </button>

                                    <a href={`/candidate-details/${candidate._id}`} className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-sm font-medium transition-colors">
                                        View Profile <ExternalLink size={14} />
                                    </a>
                                </div>

                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default JobApplicants;
